import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';

/**
 * Zero-height IntersectionObserver sentinel for mid-tree branch paging.
 *
 * When `loadMoreSentinelDisabled` is true the observer still runs but does not
 * emit, so a branch with no cursor can leave the sentinel in the DOM inertly.
 * Duplicate-load protection belongs on the consumer (`loadMore` is a no-op when
 * the cursor is null or a load is already in flight).
 */
@Directive({
  selector: '[loadMoreSentinel]',
  standalone: true,
})
export class LoadMoreSentinelDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Scroll container used as the IntersectionObserver `root`. Defaults to the viewport. */
  @Input() loadMoreSentinel: Element | null = null;

  @Input() loadMoreSentinelDisabled = false;

  @Output() loadMore = new EventEmitter<void>();

  private observer: IntersectionObserver | null = null;

  ngOnInit(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.loadMoreSentinelDisabled) {
          return;
        }

        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadMore.emit();
        }
      },
      {
        root: this.loadMoreSentinel,
        rootMargin: '200px 0px',
      },
    );

    this.observer.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
  }
}
